import React, { useState } from 'react';
import { ICellRendererParams } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import { ItemSourceMeanValidatorItemStatus as ItemStatus } from 'constants/itemSourceMeanValidatorStatus';
import ItemSourceMeanValidatorItemsModel from 'models/ItemSourceMeanValidatorItemsModel';
import ModuleContext from 'CMFW/context/ModuleContext';
import { IItemSourceMeanValidatorItem } from '../../../types/itemSourceMeanValidator';

const ItemSourceMeanValidatorActionRenderer: React.FC<ICellRendererParams> = (params) => {
  const data = params.data as IItemSourceMeanValidatorItem;
  const itemsModel = React.useContext(ModuleContext) as ItemSourceMeanValidatorItemsModel;
  const [processing, setProcessing] = useState<'keep' | 'change' | null>(null);

  if (!data) return null;
  if (data.status === ItemStatus.PENDING) return null;

  // Get decision and check for mismatch
  const aiResult = data.ai_result;
  const decision = aiResult?.decision;
  const hasEmInsertMismatch = aiResult?.has_em_insert_mismatch === true;
  const isKeep = data.decision === 'keep';
  const isChange = data.decision === 'change';
  const isExactMatch = data.decision === 'exact_match';
  const isError = decision === 'error';
  const isDone = data.status === ItemStatus.DONE;
  const hasSelection = isDone;

  // Show nothing for error or exact_match
  if (isError || isExactMatch) {
    return <ActionsContainer />;
  }

  const handleKeep = async () => {
    if (processing || (isKeep && isDone)) return;
    setProcessing('keep');
    try {
      await itemsModel.keepItem(data.id);
    } finally {
      setProcessing(null);
    }
  };
  const handleChange = async () => {
    if (processing || (isChange && isDone)) return;
    setProcessing('change');
    try {
      await itemsModel.changeItem(data.id);
    } finally {
      setProcessing(null);
    }
  };

  // Determine what buttons to show based on decision and mismatch status
  const renderButtons = () => {
    // Keep decision: only show keep button
    if (decision === 'keep') {
      return (
        <ActionButton
          onClick={handleKeep}
          disabled={processing !== null}
          $selected={isKeep && isDone}
          $hasSelection={hasSelection}
          $variant="keep"
          data-selected={isKeep && isDone ? 'true' : 'false'}
        >
          {processing === 'keep' ? <i className="fa fa-spinner fa-spin" /> : 'Keep'}
        </ActionButton>
      );
    }

    // Change decision with mismatch: show keep + manual change notice
    if (decision === 'change' && hasEmInsertMismatch) {
      return (
        <>
          <ActionButton
            onClick={handleKeep}
            disabled={processing !== null}
            $selected={isKeep && isDone}
            $hasSelection={hasSelection}
            $variant="keep"
            data-selected={isKeep && isDone ? 'true' : 'false'}
          >
            {processing === 'keep' ? <i className="fa fa-spinner fa-spin" /> : 'Keep'}
          </ActionButton>
          <MismatchNotice>
            <i className="fa fa-exclamation-circle" style={{ marginRight: 4 }} />
            Manual change needed
          </MismatchNotice>
        </>
      );
    }

    // Change decision without mismatch: show keep and change
    if (decision === 'change' && !hasEmInsertMismatch) {
      return (
        <>
          <ActionButton
            onClick={handleKeep}
            disabled={processing !== null}
            $selected={isKeep && isDone}
            $hasSelection={hasSelection}
            $variant="keep"
            data-selected={isKeep && isDone ? 'true' : 'false'}
          >
            {processing === 'keep' ? <i className="fa fa-spinner fa-spin" /> : 'Keep'}
          </ActionButton>
          <ActionButton
            onClick={handleChange}
            disabled={processing !== null}
            $selected={isChange && isDone}
            $hasSelection={hasSelection}
            $variant="change"
            data-selected={isChange && isDone ? 'true' : 'false'}
          >
            {processing === 'change' ? <i className="fa fa-spinner fa-spin" /> : 'Change'}
          </ActionButton>
        </>
      );
    }

    // Default case (shouldn't happen but just in case)
    return null;
  };

  return (
    <ActionsContainer>
      <ButtonsRow>{renderButtons()}</ButtonsRow>
    </ActionsContainer>
  );
};

export default ItemSourceMeanValidatorActionRenderer;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 5px;
  justify-content: center;
  align-items: center;
`;

const MismatchNotice = styled.div`
  padding: 6px 10px;
  background-color: #fff3e0;
  color: #e65100;
  border: 1px solid #ffcc80;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

const ActionButton = styled.button<{
  $selected?: boolean;
  $variant?: 'keep' | 'change' | 'accept';
  $hasSelection?: boolean;
}>`
  padding: 6px 14px;
  background-color: ${(p) =>
    p.$selected
      ? p.$variant === 'keep'
        ? '#66BB6A'
        : p.$variant === 'change'
        ? '#42A5F5'
        : '#8E24AA'
      : p.$variant === 'keep'
      ? '#e8f5e9'
      : p.$variant === 'change'
      ? '#e3f2fd'
      : '#f3e5f5'};
  color: ${(p) =>
    p.$selected ? '#fff' : p.$variant === 'keep' ? '#2e7d32' : p.$variant === 'change' ? '#1565c0' : '#6a1b9a'};
  border: 1px solid
    ${(p) =>
      p.$selected
        ? 'transparent'
        : p.$variant === 'keep'
        ? '#81c784'
        : p.$variant === 'change'
        ? '#64b5f6'
        : '#ce93d8'};
  border-radius: 6px;
  cursor: ${(p) => (p.$selected ? 'default' : 'pointer')};
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  opacity: ${(p) => (p.$selected ? 1 : p.$hasSelection ? 0.3 : 1)};
  min-width: 74px;
  text-align: center;
  pointer-events: ${(p) => (p.$selected ? 'none' : 'auto')};
  &:hover:not(:disabled):not([data-selected='true']) {
    opacity: 1;
    background-color: ${(p) => (p.$variant === 'keep' ? '#66BB6A' : p.$variant === 'change' ? '#42A5F5' : '#8E24AA')};
    color: #fff;
  }
  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }
`;
